'use client';

import type React from 'react';

import type { EventWebhook } from '@/types/events';
import { explorerAnalytics } from '@/lib/analytics';
import {
  formatWebhookStatus,
  getWebhookStatusColor,
} from '@/constant/webhookStatuses';
import { config } from '@/config';
import { useExplorer } from '@/hooks/useExplorer';
import { cn } from '@components/lib/utils';
import { ExternalLink } from 'lucide-react';

interface EventWebhookAttemptsProps {
  webhooks?: EventWebhook[];
}

const STATUS_BADGE_STYLES: Record<string, string> = {
  green: 'bg-green-50 text-green-700',
  red: 'bg-red-50 text-red-700',
  yellow: 'bg-yellow-50 text-yellow-700',
  gray: 'bg-gray-50 text-gray-600',
};

export const EventWebhookAttempts: React.FC<EventWebhookAttemptsProps> = ({
  webhooks,
}) => {
  const { siteName } = useExplorer();
  const webhooksSettingsUrl = siteName
    ? `${config.cbProtocol}://${siteName}.${config.cbDomain}/apikeys_and_webhooks/webhooks`
    : null;

  if (!webhooks || webhooks.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-base text-gray-400">
        No webhook attempts for this event
        {webhooksSettingsUrl && (
          <a
            href={webhooksSettingsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 transition-colors duration-200"
            onClick={() =>
              explorerAnalytics.eventExternalLinkClicked({
                source: 'manage_webhooks',
              })
            }
          >
            Manage webhooks in Chargebee
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="p-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <th className="py-2 pr-4">Webhook ID</th>
            <th className="py-2 pr-4">Status</th>
            <th className="py-2 pr-4">PC Version</th>
            <th className="py-2">Schema Type</th>
          </tr>
        </thead>
        <tbody>
          {webhooks.map((webhook) => {
            const color = getWebhookStatusColor(webhook.webhook_status);
            return (
              <tr
                key={webhook.id}
                className="border-b border-gray-100 last:border-b-0"
              >
                <td className="py-2.5 pr-4 font-mono text-xs text-gray-600">
                  {webhook.id}
                </td>
                <td className="py-2.5 pr-4">
                  <span
                    className={cn(
                      'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                      STATUS_BADGE_STYLES[color] ?? STATUS_BADGE_STYLES.gray
                    )}
                  >
                    {formatWebhookStatus(webhook.webhook_status)}
                  </span>
                </td>
                <td className="py-2.5 pr-4 text-xs text-gray-600">
                  {webhook.pc_version ?? '—'}
                </td>
                <td className="py-2.5 text-xs text-gray-600">
                  {webhook.chargebee_response_schema_type ?? '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {webhooksSettingsUrl && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <a
            href={webhooksSettingsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1 transition-colors duration-200"
            onClick={() =>
              explorerAnalytics.eventExternalLinkClicked({
                source: 'manage_webhooks',
              })
            }
          >
            Manage webhooks in Chargebee
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
    </div>
  );
};
